/*!
 * This file contains stuff for the frontend part
 *
 * @package CWP Theme Starter Kit
 * @author Demo Author <demo@example.com>
 *
 * @link https://example.com/
 !*/
 
import './assets/css/frontend.css'
import '../assets/css/frontend.scss'
import '../assets/css/contact-form-7.scss'
import '../blocks/search-results/search-results.scss'

import { cwpCasino } from './components/casino'
import { cwpCountdown } from './components/countdown'
import { cwpLatestOffers } from './components/latestOffers'

/** Run on DOM ready (no jQuery). */
function onReady(fn) {
    if (document.readyState !== 'loading') {
        fn();
    } else {
        document.addEventListener('DOMContentLoaded', fn);
    }
}

onReady(() => {
    // The campaign strip pinned above the header: its height first, as the review tabs and the offer bar
    // (cwpCasino.init) read the sticky tops it moves when they start.
    initTopStripHeight();
    // Casino blocks (load more, table of contents, filters/sort).
    cwpCasino.init();
    // Campaign countdown on toplists: shown, ticked and hidden by the visitor's clock.
    cwpCountdown.init();
    // Latest offers slide-in drawer.
    cwpLatestOffers.init();
    // Promo-code "Copy" buttons on toplist rows / bonus cards.
    initCodeCopy();
    // "More details" expanders on toplist rows.
    initMoreDetails();
    // Fixed bottom "top pick" claim bar on ranked toplists.
    initTopPickBar();
    // Publish the visible bottom bar's height for anything that must clear it.
    initBottomBarHeight();
    // Reader-facing light/dark mode controls in the site headers.
    initColorModeToggle();
    // Option A landing: accessible List / Compare view and matrix sorting.
    initLandingCompare();
});

/**
 * Two bars pin themselves to the bottom of the viewport: .cwp-sticky-cta on
 * reviews and .cwp-toppick-bar on the ranked list. Both toggle .is-visible
 * themselves; this only listens, and writes the showing bar's height to
 * --cwp-bottom-bar-h (0 when neither shows) for anything that must stay clear
 * of it: the Latest-offers pill on phones, and the footer's last links.
 *
 * The toplist bar may carry the campaign timer (Countdown::strip). Up to 782px
 * it is a strip inside the bar, so the bar's height has it; wider, it is a tab
 * standing on the bar's top edge, outside the bar's box, so its height is
 * added. The bars' class says which one shows (a MutationObserver); a
 * ResizeObserver on each bar and on its timer reads the height again whenever
 * either changes size: the timer appearing at its start or going at its end
 * (its hidden attribute, countdown.js), its text wrapping anew as the numbers
 * change (10 days to 9), a late font, a new window width.
 *
 * Its own init rather than part of the offers drawer, which returns early
 * when the drawer is switched off and would take the footer's clearance
 * with it.
 */
function initBottomBarHeight() {
    const bars = document.querySelectorAll('.cwp-sticky-cta, .cwp-toppick-bar');
    if (!bars.length) return;
    const root = document.documentElement;
    const sync = () => {
        const shown = [...bars].find((b) => b.classList.contains('is-visible'));
        const tab = shown?.querySelector('.cwp-toppick-bar__countdown:not([hidden])');
        const above = tab && getComputedStyle(tab).position === 'absolute' ? tab.offsetHeight : 0;
        root.style.setProperty('--cwp-bottom-bar-h', `${shown ? shown.offsetHeight + above : 0}px`);
    };
    const shows = new MutationObserver(sync);
    const sizes = new ResizeObserver(sync);
    bars.forEach((b) => {
        shows.observe(b, { attributes: true, attributeFilter: ['class'] });
        sizes.observe(b);
        b.querySelectorAll('.cwp-toppick-bar__countdown').forEach((tab) => sizes.observe(tab));
    });
    sync();
}

/**
 * The campaign strip pinned above the site header (Countdown::topStrip, when a
 * toplist sets countdownAtTop) writes its height to --cwp-top-strip-h, 0 while
 * it is hidden (before its start, after its end: countdown.js) or absent, for
 * everything that sticks under it: the desktop menu row, the review tabs and
 * the anchors' landing line (frontend.scss, "What stays on screen"). A
 * ResizeObserver reads it again whenever it changes size: showing at its
 * start, going at its end, its text wrapping anew, a late font, a new window
 * width. The exact height, not offsetHeight's whole pixels, so no sliver of
 * the page shows between the strip and the menu row.
 */
function initTopStripHeight() {
    const strip = document.querySelector('.cwp-top-strip');
    if (!strip) return;
    const sync = () => document.documentElement.style.setProperty('--cwp-top-strip-h', `${strip.getBoundingClientRect().height}px`);
    new ResizeObserver(sync).observe(strip);
    sync();
}

/** Behaviour contract from Option-A-Compare-Toplist.html. */
function initLandingCompare() {
    document.querySelectorAll('.cwp-casino-toplist--matrix').forEach((root) => {
        const switcher = root.querySelector('[data-landing-view-switch]');
        const buttons = [...root.querySelectorAll('[data-landing-view]')];
        const panels = [...root.querySelectorAll('[data-landing-panel]')];
        const body = root.querySelector('[data-landing-matrix-body]');
        const rows = body ? [...body.querySelectorAll('tr')] : [];

        const showView = (view) => {
            if (switcher) switcher.dataset.active = view;
            buttons.forEach((button) => {
                const active = button.dataset.landingView === view;
                button.classList.toggle('is-active', active);
                button.setAttribute('aria-selected', String(active));
            });
            panels.forEach((panel) => {
                panel.hidden = panel.dataset.landingPanel !== view;
            });
        };

        buttons.forEach((button) => {
            button.addEventListener('click', () => showView(button.dataset.landingView));
        });

        const value = (row, key) => Number.parseFloat(row.dataset[key] || '0');
        const sortRows = (key, direction = 'desc') => {
            if (!body) return;
            const multiplier = direction === 'asc' ? 1 : -1;
            [...rows]
                .sort((a, b) => (value(a, key) - value(b, key)) * multiplier)
                .forEach((row) => body.appendChild(row));
        };

        root.querySelectorAll('[data-matrix-sort]').forEach((button) => {
            button.addEventListener('click', () => {
                const key = button.dataset.matrixSort;
                const current = button.dataset.direction;
                const lowerIsBetter = key === 'wagering';
                const direction = current
                    ? (current === 'asc' ? 'desc' : 'asc')
                    : (lowerIsBetter ? 'asc' : 'desc');
                root.querySelectorAll('[data-matrix-sort]').forEach((item) => {
                    item.classList.remove('is-sorted');
                    delete item.dataset.direction;
                });
                button.classList.add('is-sorted');
                button.dataset.direction = direction;
                sortRows(key, direction);
            });
        });

        root.querySelector('[data-toplist-sort]')?.addEventListener('change', (event) => {
            const key = event.target.value === 'recommended' ? 'order' : event.target.value;
            sortRows(key, key === 'wagering' || key === 'order' ? 'asc' : 'desc');
        });

        root.querySelectorAll('[data-filter]').forEach((filter) => {
            filter.addEventListener('click', () => {
                const category = filter.dataset.filter || 'all';
                rows.forEach((row) => {
                    const categories = (row.dataset.categories || '').split(',');
                    row.hidden = category !== 'all' && !categories.includes(category);
                });
            });
        });

        showView('compare');
    });
}

/**
 * Keep every cwp/theme-toggle instance in sync and persist the reader's
 * explicit choice. The pre-paint bootstrap in Hooks\ColorMode applies the
 * initial mode before this footer bundle runs.
 */
function initColorModeToggle() {
    const root = document.documentElement;
    const toggles = [...document.querySelectorAll('[data-cwp-theme-toggle]')];
    if (!toggles.length) return;

    const config = window.cwpColorMode || {};
    const storageKey = config.storageKey || 'cwp-color-mode';
    const readPreference = () => {
        try {
            const value = window.localStorage.getItem(storageKey);
            return value === 'light' || value === 'dark' ? value : null;
        } catch (error) {
            return null;
        }
    };

    const writePreference = (mode) => {
        try {
            window.localStorage.setItem(storageKey, mode);
        } catch (error) {
            // Storage may be unavailable in private/restricted contexts. The
            // current document still switches successfully.
        }
    };

    const syncControls = (mode) => {
        const isDark = mode === 'dark';
        const label = isDark
            ? (config.switchToLight || 'Switch to light mode')
            : (config.switchToDark || 'Switch to dark mode');
        const status = isDark
            ? (config.darkModeActive || 'Dark mode active')
            : (config.lightModeActive || 'Light mode active');

        toggles.forEach((toggle) => {
            toggle.disabled = false;
            toggle.setAttribute('aria-pressed', String(isDark));
            toggle.setAttribute('aria-label', label);
            toggle.setAttribute('title', label);
            toggle.dataset.cwpColorModeStatus = status;
        });
    };

    const applyMode = (mode, persist = false) => {
        const normalized = mode === 'dark' ? 'dark' : 'light';
        root.dataset.cwpColorMode = normalized;
        root.style.colorScheme = normalized;
        syncControls(normalized);

        if (persist) writePreference(normalized);

        document.dispatchEvent(new CustomEvent('cwp:color-mode-change', {
            detail: { mode: normalized },
        }));
    };

    toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
            const next = root.dataset.cwpColorMode === 'dark' ? 'light' : 'dark';
            applyMode(next, true);
        });
    });

    applyMode(root.dataset.cwpColorMode || config.baseMode || 'light');
}

/**
 * Reveal the fixed [data-cwp-toppick] bar once the editor's-pick (#1) row has
 * scrolled out of view, hide it again when the row is visible.
 */
function initTopPickBar() {
    const bar = document.querySelector('[data-cwp-toppick]');
    if (!bar) return;
    const topRow = document.querySelector('.cwp-verdict-row--top');
    if (!topRow || !('IntersectionObserver' in window)) {
        bar.hidden = false;
        bar.classList.add('is-visible');
        return;
    }
    bar.hidden = false;
    const io = new IntersectionObserver(
        (entries) => {
            const entry = entries[0];
            const below = entry.boundingClientRect.top < 0;
            bar.classList.toggle('is-visible', !entry.isIntersecting && below);
        },
        { threshold: 0 }
    );
    io.observe(topRow);
}

/** Delegated handler for [data-cwp-more] "More details" expanders. */
function initMoreDetails() {
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cwp-more]');
        if (!btn) return;
        const row = btn.closest('.cwp-verdict-row');
        const panel = row && row.querySelector('.cwp-verdict-row__details');
        if (!panel) return;
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
    });
}

/** Delegated handler for [data-cwp-copy] promo-code copy buttons. */
function initCodeCopy() {
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cwp-copy]');
        if (!btn) return;

        const code = btn.getAttribute('data-cwp-copy') || '';
        const done = () => {
            const original = btn.textContent;
            btn.classList.add('is-copied');
            btn.textContent = btn.getAttribute('data-copied-label') || 'Copied';
            window.clearTimeout(btn._cwpCopyT);
            btn._cwpCopyT = window.setTimeout(() => {
                btn.classList.remove('is-copied');
                btn.textContent = original;
            }, 1800);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(code).then(done).catch(done);
        } else {
            done();
        }
    });
}
